<template>
  <v-container fluid>
    <h2>{{ $tt('feed.poiRecords') }}</h2>
    <v-btn color="primary" class="mt-2 mb-4" @click="openAdd()">
      <v-icon left>mdi-map-marker-plus</v-icon>
      {{ $tt('feed.addPoi') }}
    </v-btn>

    <v-progress-linear v-if="loading" indeterminate class="mb-2" />
    <p v-if="!loading && pois.length === 0" class="grey--text">{{ $tt('feed.noPoiRecords') }}</p>

    <v-row>
      <v-col v-for="r in pois" :key="r.id" cols="12" md="6" lg="4">
        <v-card>
          <img v-if="r.la != null && r.lo != null" :src="poiMapUrl(r)" class="poi-map" loading="lazy" />
          <v-card-title class="subtitle-1">
            {{ r.title }}
            <v-spacer />
            <v-btn icon small @click="openEdit(r)">
              <v-icon small>mdi-pencil-outline</v-icon>
            </v-btn>
            <v-btn icon small @click="deleteTarget = r">
              <v-icon small>mdi-delete-outline</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <div v-if="r.memo">{{ r.memo }}</div>
            <div v-if="r.photos.length" class="poi-photos mt-2">
              <img v-for="p in r.photos" :key="p.id" :src="p.thumb_url" class="poi-photo-thumb" loading="lazy" />
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn small color="primary" text @click="openShare(r)">
              <v-icon small left>mdi-share-variant</v-icon>
              {{ $tt('feed.share') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- 追加ダイアログ -->
    <v-dialog v-model="addDialog" max-width="560">
      <v-card>
        <v-card-title class="subtitle-1">{{ $tt('feed.addPoi') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="form.title" :label="$tt('feed.poiTitle')" dense outlined counter="60" />
          <v-textarea v-model="form.memo" :label="$tt('feed.recordMemo')" rows="2" dense outlined counter="1000" />
          <v-file-input
            v-model="form.photoFiles"
            :label="$tt('feed.sharePhotos')"
            accept="image/*"
            multiple
            dense
            outlined
            prepend-icon="mdi-camera"
          />
          <p class="caption grey--text mb-1">{{ $tt('feed.poiMapHint') }}</p>
          <gmap-map
            ref="poiMap"
            :center="mapCenter"
            :zoom="12"
            map-type-id="roadmap"
            style="width: 100%; height: 260px"
            :options="{ streetViewControl: false, fullscreenControl: false }"
            @click="onMapClick"
          >
            <gmap-marker v-if="form.la != null" :position="{ lat: form.la, lng: form.lo }" />
          </gmap-map>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="addDialog = false">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!form.title" @click="save()">
            {{ $tt('customSaveAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 編集ダイアログ -->
    <v-dialog :value="!!editTarget" max-width="480" @input="editTarget = null">
      <v-card v-if="editTarget">
        <v-card-title class="subtitle-1">{{ $tt('feed.poiRecords') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="editForm.title" :label="$tt('feed.poiTitle')" dense outlined counter="60" />
          <v-textarea v-model="editForm.memo" :label="$tt('feed.recordMemo')" rows="3" dense outlined counter="1000" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="editTarget = null">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!editForm.title" @click="saveEdit()">
            {{ $tt('customSaveAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 削除確認 -->
    <v-dialog :value="!!deleteTarget" max-width="360" @input="deleteTarget = null">
      <v-card>
        <v-card-text class="pt-4">{{ $tt('feed.recordDeleteConfirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteTarget = null">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="error" text @click="deletePoi()">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <share-post-dialog
      v-model="shareDialog"
      :hidePhotoInput="true"
      :initialPhotoIds="sharePhotoIds"
      :initialText="shareText"
      :cellOverride="shareCell"
    />
  </v-container>
</template>

<script>
import SharePostDialog from '@/components/feed/SharePostDialog'
import * as Constants from '@/lib/constants'
import feedApiClient from '@/lib/feed-api-client'
import Toast from '@/lib/toast'

export default {
  name: 'PoiRecords',
  components: { SharePostDialog },
  data() {
    return {
      loading: false,
      saving: false,
      records: [],
      addDialog: false,
      deleteTarget: null,
      mapCenter: { lat: 42.985, lng: 143.2 },
      form: this.emptyForm(),
      editTarget: null,
      editForm: { title: '', memo: '' },
      shareDialog: false,
      sharePhotoIds: [],
      shareText: '',
      shareCell: null,
    }
  },
  computed: {
    pois() {
      return this.records.filter(r => r.kind === 'poi')
    },
  },
  mounted() {
    this.load()
    // 圃場の重心 (GeoJSON Point、coordinates=[lat, lon]) があれば地図の初期位置に使う
    const c = this.$store.getters.centroidOfUserFields
    if (c && c.geometry && c.geometry.coordinates && c.geometry.coordinates.length >= 2) {
      this.mapCenter = { lat: c.geometry.coordinates[0], lng: c.geometry.coordinates[1] }
    }
  },
  methods: {
    emptyForm() {
      return { title: '', memo: '', photoFiles: [], la: null, lo: null }
    },
    openAdd() {
      this.form = this.emptyForm()
      this.addDialog = true
    },
    onMapClick(e) {
      this.form.la = e.latLng.lat()
      this.form.lo = e.latLng.lng()
    },
    poiMapUrl(r) {
      return (
        'https://maps.googleapis.com/maps/api/staticmap?size=400x160&maptype=hybrid&zoom=15&markers=color:green%7C' +
        r.la +
        ',' +
        r.lo +
        '&key=' +
        Constants.GOOGLE_MAP_API_KEY
      )
    },
    async load() {
      this.loading = true
      try {
        const res = await feedApiClient.manualRecords()
        this.records = res.data.records || []
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.loadError')
      } finally {
        this.loading = false
      }
    },
    async save() {
      this.saving = true
      try {
        const photoIds = []
        for (const f of (this.form.photoFiles || []).slice(0, 4)) {
          const res = await feedApiClient.uploadPhoto(f)
          photoIds.push(res.data.id)
        }
        await feedApiClient.createManualRecord({
          kind: 'poi',
          title: this.form.title,
          memo: this.form.memo,
          la: this.form.la != null ? this.form.la : undefined,
          lo: this.form.lo != null ? this.form.lo : undefined,
          photo_ids: photoIds.length ? photoIds : undefined,
        })
        Toast.i18nSuccess('feed.recordCreated')
        this.addDialog = false
        this.load()
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.shareFailed')
      } finally {
        this.saving = false
      }
    },
    openEdit(r) {
      this.editForm = { title: r.title || '', memo: r.memo || '' }
      this.editTarget = r
    },
    async saveEdit() {
      this.saving = true
      try {
        await feedApiClient.updateManualRecord(this.editTarget.id, {
          title: this.editForm.title,
          memo: this.editForm.memo,
        })
        this.editTarget = null
        this.load()
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.shareFailed')
      } finally {
        this.saving = false
      }
    },
    openShare(r) {
      // 位置はフィードの原則どおりメッシュ粒度 (約11km) に落として渡す
      this.sharePhotoIds = (r.photos || []).map(p => p.id)
      this.shareText = [r.title, r.memo].filter(Boolean).join('\n')
      this.shareCell = r.la != null && r.lo != null ? { la: Math.floor(r.la * 10), lo: Math.floor(r.lo * 10) } : null
      this.shareDialog = true
    },
    async deletePoi() {
      const t = this.deleteTarget
      if (!t) return
      try {
        await feedApiClient.deleteManualRecord(t.id)
        this.records = this.records.filter(r => r.id !== t.id)
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.shareFailed')
      } finally {
        this.deleteTarget = null
      }
    },
  },
}
</script>

<style scoped>
.poi-map {
  width: 100%;
  height: 160px;
  object-fit: cover;
  display: block;
}
.poi-photos {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.poi-photo-thumb {
  width: calc(50% - 3px);
  max-width: 180px;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 6px;
}
</style>
